// Marca do Portal do Consumidor (#6117): qual marca a sessão vê e o tema de cor.
//
// Logado como consumidor: quem decide é o banco (portal_consumidor_marca() — mais UCs da
// parceira que da Vigor, ou empate, vira a marca da parceira). Visitante: a URL de login
// (…/portal-consumidor/gran). A marca fica em memória depois do logout, para o "Sair" do
// cliente da Gran voltar para a tela de entrada da Gran.
//
// Falha ao consultar a marca (rede, migration ainda não aplicada): fica a da Vigor e o
// portal segue funcionando — nunca trava o cliente fora.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { MARCAS, marcaDaRota, marcaPorChave, type MarcaPortal } from './marcaPortal';
import { CORES_GRAN, cssTemaMarca } from './temaMarca';

interface Valor {
  marca: MarcaPortal;
  /** Ainda consultando a marca do consumidor logado — não pinte a tela com a marca errada. */
  carregando: boolean;
}

const MarcaPortalContext = createContext<Valor>({ marca: MARCAS.vigor, carregando: false });

export const useMarcaPortal = () => useContext(MarcaPortalContext);

const CSS_POR_MARCA: Record<string, string> = { gran: cssTemaMarca('gran', CORES_GRAN) };

/** Rota de layout que envolve todas as rotas do portal (App.tsx). */
export const MarcaPortalLayout: React.FC = () => {
  const { user, profile, loading } = useAuth();
  const { pathname } = useLocation();
  const daRota = marcaDaRota(pathname);
  const [doBanco, setDoBanco] = useState<string | null>(null);
  const [consultando, setConsultando] = useState(false);
  // Última marca que valeu nesta aba: é o que manda o "Sair" da Gran para a entrada da Gran.
  const [ultima, setUltima] = useState<string | null>(null);

  const consumidorId = user && profile?.role === 'consumidor' ? user.id : null;

  useEffect(() => {
    if (!consumidorId) { setDoBanco(null); return; }
    let vivo = true;
    setConsultando(true);
    supabase.rpc('portal_consumidor_marca')
      .then(({ data, error }) => {
        if (!vivo) return;
        if (error) console.error('[Portal] marca do consumidor:', error);
        const chave = error ? 'vigor' : String(data || 'vigor');
        setDoBanco(chave);
        setUltima(chave);
      })
      .then(() => { if (vivo) setConsultando(false); }, () => { if (vivo) setConsultando(false); });
    return () => { vivo = false; };
  }, [consumidorId]);

  const chave = consumidorId ? (doBanco ?? daRota ?? ultima) : (daRota ?? ultima);
  const marca = marcaPorChave(chave);
  const carregando = loading || (!!consumidorId && (consultando || doBanco === null));

  // Tema de cor no <body> (os modais do portal abrem fora da árvore da página).
  useEffect(() => {
    const css = CSS_POR_MARCA[marca.chave];
    if (!css) return;
    const classe = `marca-${marca.chave}`;
    document.body.classList.add(classe);
    let style = document.getElementById('tema-marca-portal') as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = 'tema-marca-portal';
      document.head.appendChild(style);
    }
    style.textContent = css;
    return () => { document.body.classList.remove(classe); };
  }, [marca.chave]);

  // Ícone da aba com a marca do portal (a Gran não vê o ícone da Vigor). Volta ao sair.
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link || !marca.favicon) return;
    const anterior = link.href;
    link.href = marca.favicon;
    return () => { link.href = anterior; };
  }, [marca.favicon]);

  const valor = useMemo(() => ({ marca, carregando }), [marca, carregando]);
  return (
    <MarcaPortalContext.Provider value={valor}>
      <Outlet />
    </MarcaPortalContext.Provider>
  );
};

/** Título da aba: "Minhas Faturas — Gran Energia". Restaura o anterior ao sair. */
export function useTituloPortal(pagina: string) {
  const { marca } = useMarcaPortal();
  useEffect(() => {
    const anterior = document.title;
    document.title = `${pagina} — ${marca.tituloAba}`;
    return () => { document.title = anterior; };
  }, [pagina, marca.tituloAba]);
}
